import { forwardRef } from 'react';
import cx from 'classnames';
import './FilterChip.css';

/**
 * QHS FilterChip v2
 * -----------------
 * Pill-shaped filter trigger for analytics dashboards. Label is always
 * visible; the value slot shows the current selection (or a placeholder
 * when empty). Click the pill to open its popover; the caller owns the
 * popover — FilterChip is a trigger, not a dropdown.
 *
 *   <FilterChip label="Date" value="Apr 1–30" active={open}
 *               onClick={() => setOpen(true)}
 *               onClear={handleClear} />   // onClear is optional;
 *                                         // shows X when a value is set
 *
 * States:
 *   - empty        (no value): muted styling, trailing chevron
 *   - filled       (value set): brand tint, trailing X if onClear given
 *   - active       (popover open): brand border + background, regardless
 *                  of fill state
 *
 * The component is a `<button>` so it gets focus + keyboard activation
 * for free. An inner `<span>` carries the X-clear so `event.stopPropagation`
 * keeps the parent popover from re-opening on clear.
 */
const ChevronIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const FilterChip = forwardRef(function FilterChip(
  {
    label,
    value,
    placeholder,
    active = false,
    disabled = false,
    onClear,
    onClick,
    className,
    children,
    ...rest
  },
  ref
) {
  const hasValue = value != null && value !== '';
  const showClear = hasValue && typeof onClear === 'function';

  return (
    <button
      ref={ref}
      type="button"
      className={cx(
        'qhs-filter-chip',
        hasValue && 'qhs-filter-chip--filled',
        active && 'qhs-filter-chip--active',
        disabled && 'qhs-filter-chip--disabled',
        className
      )}
      disabled={disabled}
      onClick={onClick}
      aria-haspopup="true"
      aria-expanded={active}
      {...rest}
    >
      <span className="qhs-filter-chip__label">{label}</span>
      <span className="qhs-filter-chip__divider" aria-hidden="true" />
      <span
        className={cx(
          'qhs-filter-chip__value',
          !hasValue && 'qhs-filter-chip__value--placeholder'
        )}
      >
        {hasValue ? (children ?? value) : (placeholder ?? '—')}
      </span>
      {showClear ? (
        <span
          className="qhs-filter-chip__clear"
          role="button"
          tabIndex={0}
          aria-label={`Clear ${label}`}
          onClick={(event) => {
            event.stopPropagation();
            onClear(event);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.stopPropagation();
              onClear(event);
            }
          }}
        >
          <XIcon />
        </span>
      ) : (
        <span className="qhs-filter-chip__chevron" aria-hidden="true">
          <ChevronIcon />
        </span>
      )}
    </button>
  );
});

export default FilterChip;
